// Venson Driver Assist — shared constants & helpers

export const INTERNAL_TO_PUBLIC = {
  new_request: "Request received",
  assigned_to_pod: "Request received",
  garage_contacted: "Booking being arranged",
  awaiting_garage_response: "Booking being arranged",
  appointment_confirmed: "Booking confirmed",
  vehicle_arrived: "Vehicle with garage",
  awaiting_diagnosis: "Garage assessing vehicle",
  awaiting_internal_authority: "Work being reviewed",
  authority_granted: "Work approved",
  parts_ordered: "Waiting for parts",
  work_in_progress: "Work in progress",
  vehicle_complete: "Ready for collection",
  completed: "Completed",
};

export const INTERNAL_STATUSES = Object.keys(INTERNAL_TO_PUBLIC).map((k) => ({
  value: k,
  label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  public: INTERNAL_TO_PUBLIC[k],
}));

// ordered public timeline stages
export const TIMELINE_STAGES = [
  "Request received",
  "Booking being arranged",
  "Booking confirmed",
  "Vehicle with garage",
  "Garage assessing vehicle",
  "Work being reviewed",
  "Work approved",
  "Waiting for parts",
  "Work in progress",
  "Ready for collection",
  "Completed",
];

export const REQUEST_TYPES = [
  { value: "service", label: "Service" },
  { value: "mot", label: "MOT" },
  { value: "repair", label: "Service & Repair" },
  { value: "tyres", label: "Tyres" },
  { value: "windscreen", label: "Windscreen & Glass" },
  { value: "breakdown", label: "Breakdown" },
  { value: "accident", label: "Accident & Bodywork" },
  { value: "hire_vehicle", label: "Hire vehicle" },
  { value: "other", label: "General enquiry" },
];

export const REQUEST_LABEL = Object.fromEntries(REQUEST_TYPES.map((r) => [r.value, r.label]));
// legacy / category values that may appear in older rows
Object.assign(REQUEST_LABEL, {
  service_mot: "Service / MOT",
  service_repair: "Service & Repair",
  windscreen_glass: "Windscreen & Glass",
  accident_bodywork: "Accident & Bodywork",
});

// ---- Driver Mode Round 1 request-flow taxonomy ----
// Service & Repair sub-types. Each maps to a stored request_type so existing
// queues/history keep working; the granular choice is kept in request_details.
export const SERVICE_TYPES = [
  { value: "full_service", label: "Full service", request_type: "service" },
  { value: "interim_service", label: "Interim / oil service", request_type: "service" },
  { value: "mot", label: "MOT", request_type: "mot" },
  { value: "service_and_mot", label: "Service + MOT together", request_type: "service" },
  { value: "warning_light", label: "Warning light / diagnostic", request_type: "repair" },
  { value: "mechanical", label: "Mechanical fault", request_type: "repair" },
  { value: "electrical", label: "Electrical fault", request_type: "repair" },
  { value: "air_con", label: "Air-conditioning", request_type: "repair" },
  { value: "recall", label: "Manufacturer recall", request_type: "repair" },
  { value: "other_repair", label: "Other repair", request_type: "repair" },
];

// UK tyre positions (nearside = kerb/passenger side, offside = driver side).
export const TYRE_POSITIONS = [
  { value: "osf", label: "Offside front", hint: "Driver front" },
  { value: "nsf", label: "Nearside front", hint: "Passenger front" },
  { value: "osr", label: "Offside rear", hint: "Driver rear" },
  { value: "nsr", label: "Nearside rear", hint: "Passenger rear" },
  { value: "spare", label: "Spare", hint: "Spare wheel" },
];

export const TYRE_ISSUES = [
  { value: "puncture", label: "Puncture / slow deflation" },
  { value: "flat", label: "Completely flat" },
  { value: "worn", label: "Worn / low tread" },
  { value: "damage", label: "Sidewall / kerb damage" },
  { value: "tpms", label: "Pressure warning light" },
];

export const GLASS_AREAS = [
  { value: "windscreen", label: "Windscreen" },
  { value: "rear_window", label: "Rear window" },
  { value: "driver_door", label: "Driver door glass" },
  { value: "passenger_door", label: "Passenger door glass" },
  { value: "sunroof", label: "Sunroof / panoramic roof" },
  { value: "other_glass", label: "Other glass" },
];

export const GLASS_DAMAGE = [
  { value: "chip", label: "Small chip (repairable)" },
  { value: "crack", label: "Crack" },
  { value: "shattered", label: "Shattered / hole" },
];

// Hire (#14). PROVISIONAL classes — official Venson daily-hire class codes will
// replace HIRE_CLASSES cleanly when supplied (data model already isolates them).
export const HIRE_VEHICLE_TYPES = [
  { value: "car", label: "Car" },
  { value: "van", label: "Van" },
];
export const HIRE_CLASSES = {
  car: [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
    { value: "suv", label: "SUV" },
  ],
  van: [
    { value: "v1", label: "V1 · Car-derived van" },
    { value: "v2", label: "V2 · Short-wheelbase (SWB)" },
    { value: "lwb", label: "Long-wheelbase (LWB)" },
    { value: "xlwb", label: "Extra-long-wheelbase (XLWB)" },
  ],
};
export const HIRE_PROVISIONAL_NOTE = "Provisional categories — final Venson hire classes to follow.";

export function formatReg(reg) {
  if (!reg) return "";
  return reg.toUpperCase();
}

// Canonical registration identity: uppercase, alphanumeric only (spaces and
// separators removed). Registrations with/without spaces resolve identically.
export function normalizeReg(reg) {
  return (reg || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export function fmtDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export function timeAgo(d) {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const h = Math.floor(diff / 3.6e6);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

// 24h update queue bucket based on next_update_due_at
export function updateBucket(due) {
  if (!due) return { key: "none", label: "No deadline", tone: "muted" };
  const hrs = (new Date(due).getTime() - Date.now()) / 3.6e6;
  if (hrs < -8) return { key: "overdue", label: "Overdue", tone: "red" };
  if (hrs <= 0) return { key: "due_now", label: "Update required", tone: "amber" };
  if (hrs <= 4) return { key: "due_soon", label: "Update due soon", tone: "amber" };
  return { key: "scheduled", label: "Scheduled", tone: "green" };
}

export const ROLE_LABEL = {
  driver: "Driver",
  account_executive: "Account Executive",
  senior_account_executive: "Senior Account Executive",
  account_manager: "Account Manager",
  pod_lead: "Pod Lead",
  administrator: "Administrator",
  external_fleet_manager: "Fleet Manager",
};

export const OPEN_STATUSES = INTERNAL_STATUSES.filter((s) => s.value !== "completed").map((s) => s.value);

export const APP_BUILD = "6.1.0-uat";

// ---- Sprint 2.1: authoritative-deadline display helpers ----
// These ONLY format the stored next_update_due_at against current Europe/London
// time. No deadline is recalculated in React — the DB is the source of truth.
function _lonParts(x) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", weekday: "short", year: "numeric",
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  });
  return Object.fromEntries(fmt.formatToParts(x).map((p) => [p.type, p.value]));
}
function _lonDayKey(x) { const p = _lonParts(x); return `${p.year}-${p.month}-${p.day}`; }

function _fmtDuration(ms) {
  const m = Math.round(ms / 60000);
  const days = Math.floor(m / 1440);
  const hrs = Math.floor((m % 1440) / 60);
  const mins = m % 60;
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  return `${mins}m`;
}
function _lonDayTime(d, now) {
  const p = _lonParts(d);
  const t = `${p.hour}:${p.minute}`;
  const dk = _lonDayKey(d), nk = _lonDayKey(now);
  if (dk === nk) return `today · ${t}`;
  if (dk === _lonDayKey(new Date(now.getTime() + 86400000))) return `tomorrow · ${t}`;
  return `${p.weekday} · ${t}`;
}

export function isDueLondonToday(due) {
  if (!due) return false;
  return _lonDayKey(new Date(due)) === _lonDayKey(new Date());
}

// Returns { state, tone, label, srLabel } for a stored deadline string.
// states: not_due | due_soon | due_now | overdue | none
export function deadlineInfo(due, now = new Date()) {
  if (!due) return { state: "none", tone: "muted", label: "Deadline not set", srLabel: "No deadline set" };
  const d = new Date(due);
  const ms = d.getTime() - now.getTime();
  const H = 3.6e6;
  if (ms < 0) {
    const dur = _fmtDuration(-ms);
    return { state: "overdue", tone: "red", label: `Overdue by ${dur}`, srLabel: `Overdue by ${dur}` };
  }
  if (ms <= 2 * H) {
    const label = ms <= 15 * 60000 ? "Update required" : `Due in ${_fmtDuration(ms)}`;
    return { state: "due_now", tone: "amber-strong", label, srLabel: `Update due now, ${label}` };
  }
  if (ms <= 8 * H) {
    const w = _lonDayTime(d, now);
    return { state: "due_soon", tone: "amber", label: `Due ${w}`, srLabel: `Due soon, ${w}` };
  }
  const w = _lonDayTime(d, now);
  return { state: "not_due", tone: "neutral", label: `Due ${w}`, srLabel: `Not yet due, ${w}` };
}

// filter match for the deadline quick-filters (open bookings only)
export function matchesDeadlineFilter(booking, filter) {
  if (!filter) return true;
  if (booking.internal_status === "completed") return false;
  const info = deadlineInfo(booking.next_update_due_at);
  if (filter === "none") return info.state === "none";
  if (filter === "overdue") return info.state === "overdue";
  if (filter === "due_soon") return info.state === "due_soon" || info.state === "due_now";
  if (filter === "today") return isDueLondonToday(booking.next_update_due_at);
  return true;
}
